import React from "react";
import {
  Chapter as RPDFChapter,
  ChapterProps,
  TextNode as RPDFCTextNode,
  TextNode,
  View,
} from "@surge-global-engineering/react-pdf-components";
import { StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import {
  PdfChapter,
  PdfExpandedBook,
  PdfTheme,
  PdfExtendedTOCSummaryItem,
} from "../types";
import { getThemeConfigsForChapterTypes } from "../configs/chapter-theme-configs";
import { getLayoutChapterProperties } from "../helpers/layout-chapter-properties";
import ChapterTitleCard, { ChapterTitleCardProps } from "./ChapterTitleCard";

interface TOCProps {
  chapter: PdfChapter;
  book: PdfExpandedBook;
  theme: PdfTheme;
  chapterProps: ChapterProps;
  chapterNumber?: number;
  tocSummary: PdfExtendedTOCSummaryItem[];
}

interface TOCNumberProps {
  isPlaceholder: boolean;
  fontSize?: number;
  fontFamily?: string;
}

const shouldWrap = (subheads): boolean => {
  if ( subheads && subheads.length > 2) {
    return true;
  }

  return false;
};

const TOCNumber: React.FC<TOCNumberProps> = ({
  isPlaceholder,
  fontFamily,
  fontSize,
  children,
}) => {
  if (children === "") {
    return null;
  }
  const color = isPlaceholder ? "#ffffff" : "#000000";
  return (
    <RPDFCTextNode
      fontSize={fontSize}
      style={{ fontFamily, color, paddingRight: 5 }}
    >
      {children}.{" "}
    </RPDFCTextNode>
  );
};

const TOC: React.FC<TOCProps> = ({
  chapter,
  book,
  tocSummary,
  theme,
  chapterProps,
  chapterNumber,
}) => {
  const { title, type } = chapter;
  const { title: bookTitle, author } = book;
  const { layout, baseFontSize, printBaseFont } = theme.properties;
  const themeConfigsForChapterTypes = getThemeConfigsForChapterTypes(type);

  const getFormatedlayoutChapterProperties = () => {
    const layoutData = getLayoutChapterProperties(
      layout,
      author.join(", ")?.toUpperCase(),
      bookTitle?.toUpperCase(),
      title?.toUpperCase()
    );
    const { headerVisibility } = themeConfigsForChapterTypes;
    return {
      ...layoutData,
      evenPageHeaderText:
        headerVisibility === "hide" ? "" : layoutData.evenPageHeaderText,
      oddPageHeaderText:
        headerVisibility === "hide" ? "" : layoutData.oddPageHeaderText,
    };
  };

  const handlePageNumberTransformation = (pageNumber: number) => {
    if (themeConfigsForChapterTypes.footerVisibility === "hide") return "";
    return pageNumber.toString();
  };

  const props: ChapterProps = {
    ...chapterProps,
    headerHiddenPages: [1],
    getTransformedPageNumber: handlePageNumberTransformation,
    ...getFormatedlayoutChapterProperties(),
  };

  const chapterTitleCardProps: ChapterTitleCardProps = {
    chapter: { ...chapter, title: chapter.title ? chapter.title : "Contents" },
    theme,
    // styles: { ...styles, minHeight: baseFontSize * 8 } as any,
    chapterNumber,
  };

  const _styles = StyleSheet.create({
    tocItemContainer: {
      marginBottom: baseFontSize * 0.4,
      lineHeight: 1.6,
    },
    tocItemContainerRow: {
      display: "flex",
      flexDirection: "row",
      lineHeight: 1.6,
    },
    text: {
      fontFamily: printBaseFont,
    },
    subheadText: {
      marginLeft: baseFontSize * 1,
    },
    titleRow: {
      alignContent: "space-between",
      width: "100%",
    },
    titleText: {
      width: "80%",
    },
    contentText: {
      width: "85%",
      textAlign: "justify",
    },
    titleRowLeft: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
    },
    titleRowRight: {
      alignSelf: "flex-start",
      textAlign: "right",
    },
    rowLeftMargin: {
      marginLeft: baseFontSize * 1.5,
  },
  });

  const MarginLeft = ({ children, depth }) => {
    if (depth === 0) 
      return <View>{children}</View>;
    else
      return <View style={_styles.rowLeftMargin}><MarginLeft depth={depth-1} >{children}</MarginLeft></View>;
  };

  return (
    <RPDFChapter {...props} style={{ fontFamily: printBaseFont }}>
      <ChapterTitleCard {...chapterTitleCardProps} />
      {tocSummary.map((tocItem, index) => {
        const { chapterNumber, title, subheads, subtitle, pageNumberText,depth } =
          tocItem;
        return (
          <View
            key={`toc-item-${index}`}
            style={_styles.tocItemContainer}
            // content could be pushed to the next page if subheads count is at most 2
            wrap={shouldWrap(subheads)}
          >
            <MarginLeft depth={depth}>
              <View style={[_styles.tocItemContainerRow, _styles.titleRow]}>
                <View style={_styles.titleRowLeft}>
                  <TOCNumber
                    isPlaceholder={false}
                    fontFamily={printBaseFont}
                    fontSize={baseFontSize}
                  >
                    {chapterNumber}
                  </TOCNumber>
                  <TextNode
                    fontSize={baseFontSize}
                    style={[_styles.text, _styles.titleText]}
                  >
                    {title}
                  </TextNode>
                </View>
                <View style={_styles.titleRowRight}>
                  <TextNode fontSize={baseFontSize} style={_styles.text}>
                    {pageNumberText || " "}
                  </TextNode>
                </View>
              </View>
              {!!subtitle && (
                <View style={_styles.tocItemContainerRow}>
                  <TOCNumber
                    isPlaceholder={true}
                    fontFamily={printBaseFont}
                    fontSize={baseFontSize}
                  >
                    {chapterNumber}
                  </TOCNumber>
                  <TextNode
                    fontSize={baseFontSize}
                    style={[
                      _styles.contentText,
                      _styles.text,
                    ]}
                  >
                    {subtitle}
                  </TextNode>
                </View>
              )}
              {!!subheads?.length &&
                subheads.map((subhead, sIndex) => {
                  return (
                    <View
                      key={`toc-subhead-${index}-${sIndex}`}
                      style={_styles.tocItemContainerRow}
                    >
                      <TOCNumber
                        isPlaceholder={true}
                        fontFamily={printBaseFont}
                        fontSize={baseFontSize}
                      >
                        {chapterNumber}
                      </TOCNumber>
                      <TextNode
                        fontSize={baseFontSize}
                        style={[
                          _styles.text,
                          _styles.subheadText,
                          _styles.contentText,
                        ]}
                      >
                        {subhead.text}
                      </TextNode>
                    </View>
                  );
                })}
            </MarginLeft>
          </View>
        );
      })}
    </RPDFChapter>
  );
};

export default TOC;
